import { Component, DestroyRef, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PortalService } from './portal.service';
import { ToastService } from '../../../services/notification/toast.service';
import { SecurityService } from '../../../services/security.service';
import { WebService } from '../../../services/web/web.service';
import { PageHeaderComponent } from '../../../common/page-header/page-header.component';
import { AdminSessionSwitcherComponent } from '../../../admin-session-switcher/admin-session-switcher.component';
import { PortalListComponent } from '../portal-list/portal-list.component';
import { KpiDashboardComponent } from '../kpi-dashboard/kpi-dashboard.component';
import { RouterModule } from '@angular/router';

type MyPortalsTab = 'services' | 'kpi';
type MyPortalsTabPreference = 'services_first' | 'kpi_first' | null;

interface MyPortalsTabConfig {
    id: MyPortalsTab;
    labelKey: string;
}

@Component({
    selector: 'app-my-portals',
    templateUrl: './my-portals.component.html',
    styleUrls: ['./my-portals.component.scss'],
    standalone: true,
    imports: [CommonModule, RouterModule, MatDialogModule, PageHeaderComponent, AdminSessionSwitcherComponent, PortalListComponent, KpiDashboardComponent, TranslateModule]
})
export class MyPortalsComponent implements OnInit {
    @ViewChild('tabPreferenceDialog') tabPreferenceDialog?: TemplateRef<unknown>;
    activeTab: MyPortalsTab = 'services';
    kpiTabLoaded = false;
    orderedTabs: MyPortalsTabConfig[] = [];
    partnerId: number | null = null;
    hasPortals = false;
    selectedTabPreference: Exclude<MyPortalsTabPreference, null> = 'services_first';
    private partnerSetting: any;
    private tabPreferenceDialogRef?: MatDialogRef<unknown>;
    private tabPreferenceSaved = false;
    private destroyRef = inject(DestroyRef);
    private readonly tabs: Record<MyPortalsTab, MyPortalsTabConfig> = {
        services: {
            id: 'services',
            labelKey: 'myPortals.tabs.services'
        },
        kpi: {
            id: 'kpi',
            labelKey: 'myPortals.tabs.kpi'
        }
    };

    constructor(private securityService: SecurityService,
                private portalService: PortalService,
                private webService: WebService,
                private toast: ToastService,
                private translate: TranslateService,
                private dialog: MatDialog) {
    }

    get isEmpty(): boolean {
        return !this.hasPortals;
    }

    ngOnInit(): void {
        this.portalService.hasPortals
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(has => this.hasPortals = has);

        this.partnerId = this.securityService.getTokenPartner();

        if (this.partnerId == null) {
            this.applyTabPreference(null);
            this.toast.showInfo(this.translate.instant('myPortals.messages.noSubscriptions'));
            this.openTabPreferenceDialogIfNeeded(null);
            return;
        }

        this.applyTabPreference(null);
        this.loadTabPreference();
    }

    setActiveTab(tab: MyPortalsTab): void {
        this.activeTab = tab;
        this.markTabLoaded(tab);
    }

    selectInitialTabPreference(preference: Exclude<MyPortalsTabPreference, null>): void {
        this.selectedTabPreference = preference;
    }

    saveInitialTabPreference(): void {
        this.saveTabPreference(this.selectedTabPreference);
        this.tabPreferenceSaved = true;
        this.tabPreferenceDialogRef?.close();
    }

    closeTabPreferenceDialog(): void {
        this.saveTabPreference('services_first');
        this.tabPreferenceSaved = true;
        this.tabPreferenceDialogRef?.close();
    }

    private openTabPreferenceDialog(): void {
        if (!this.tabPreferenceDialog || this.tabPreferenceDialogRef) {
            return;
        }

        this.tabPreferenceSaved = false;
        this.selectedTabPreference = 'services_first';
        this.tabPreferenceDialogRef = this.dialog.open(this.tabPreferenceDialog, {
            autoFocus: false,
            maxWidth: '780px',
            width: '92vw'
        });

        this.tabPreferenceDialogRef.afterClosed().subscribe(() => {
            this.tabPreferenceDialogRef = undefined;
            if (!this.tabPreferenceSaved) {
                this.saveTabPreference('services_first');
            }
        });
    }

    private openTabPreferenceDialogIfNeeded(preference: MyPortalsTabPreference): void {
        if (preference === null) {
            setTimeout(() => this.openTabPreferenceDialog());
        }
    }

    private loadTabPreference(): void {
        this.webService.get(`${this.webService.HOST}/partnerSetting/partner/${this.partnerId}`)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: response => {
                    this.partnerSetting = response?.result;
                    const preference = this.normalizeTabPreference(response?.result?.myPortalsDefaultTab);
                    this.applyTabPreference(preference);
                    this.openTabPreferenceDialogIfNeeded(preference);
                },
                error: err => {
                    this.applyTabPreference(null);
                    this.openTabPreferenceDialogIfNeeded(null);
                    this.toast.showError(err);
                }
            });
    }

    private saveTabPreference(preference: Exclude<MyPortalsTabPreference, null>): void {
        this.applyTabPreference(preference);
        if (!this.partnerSetting?.id) {
            return;
        }

        const payload = {
            ...this.partnerSetting,
            myPortalsDefaultTab: preference,
            partner: {
                id: this.partnerSetting.partner?.id || this.partnerId
            }
        };
        delete payload.createdAt;
        delete payload.updatedAt;

        this.webService.put(payload, `${this.webService.HOST}/partnerSetting/${this.partnerSetting.id}`)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: response => {
                    this.partnerSetting = response?.result || payload;
                },
                error: err => {
                    this.toast.showError(err);
                }
            });
    }

    private normalizeTabPreference(value: unknown): MyPortalsTabPreference {
        return value === 'services_first' || value === 'kpi_first' ? value : null;
    }

    private applyTabPreference(preference: MyPortalsTabPreference): void {
        if (preference === 'kpi_first') {
            this.orderedTabs = [this.tabs.kpi, this.tabs.services];
            this.activeTab = 'kpi';
            this.markTabLoaded('kpi');
            return;
        }

        this.orderedTabs = [this.tabs.services, this.tabs.kpi];
        this.activeTab = 'services';
    }

    private markTabLoaded(tab: MyPortalsTab): void {
        if (tab === 'kpi') {
            this.kpiTabLoaded = true;
        }
    }
}
